/**
 * uiSound — synthesised UI click/whoosh tones via Web Audio.
 *
 * Lightweight enough to not need any sound files. Three flavours:
 *   - 'click'    short 1 kHz blip (tab switch)
 *   - 'whoosh'   downward sweep 1.2 kHz → 400 Hz (panel/modal open)
 *   - 'close'    short upward blip (modal close)
 *
 * Honours the `playSounds` pref through `enabled()`. Each call is a
 * fire-and-forget; we re-use a singleton AudioContext.
 */
import { loadPrefs } from './prefs.js';

let ctx: AudioContext | null = null;
function getCtx(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!ctx) {
        try {
            const AC = (window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
            if (!AC) return null;
            ctx = new AC();
        } catch { return null; }
    }
    return ctx;
}

export type UiSoundKind = 'click' | 'whoosh' | 'close';

function enabled(): boolean {
    try { return loadPrefs().playSounds; } catch { return true; }
}

export function playUi(kind: UiSoundKind): void {
    if (!enabled()) return;
    const c = getCtx();
    if (!c) return;
    if (c.state === 'suspended') { c.resume().catch(() => {}); }
    const now = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain).connect(c.destination);
    gain.gain.value = 0.06;        // quiet by default
    if (kind === 'click') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1000, now);
        gain.gain.linearRampToValueAtTime(0, now + 0.05);
        osc.start(now); osc.stop(now + 0.06);
    } else if (kind === 'whoosh') {
        osc.type = 'sine';
        osc.frequency.setValueAtTime(1200, now);
        osc.frequency.exponentialRampToValueAtTime(400, now + 0.18);
        gain.gain.linearRampToValueAtTime(0, now + 0.18);
        osc.start(now); osc.stop(now + 0.2);
    } else {
        // close: short rising blip
        osc.type = 'sine';
        osc.frequency.setValueAtTime(600, now);
        osc.frequency.exponentialRampToValueAtTime(1200, now + 0.08);
        gain.gain.linearRampToValueAtTime(0, now + 0.08);
        osc.start(now); osc.stop(now + 0.1);
    }
}
